import React, { useEffect, useState } from 'react';

import pluralize from 'pluralize';
import { useLocation } from 'react-router-dom';

import { Alert, Typography } from '@mui/material';

import useAppNavigation from '~/hooks/useAppNavigation';

import LedOffersService from '~/services/LedOffersService';
import OffersService from '~/services/OffersService';
import VendorsService from '~/services/VendorsService';

import { addQuery, parseSearchString } from '~/utils/url';

import OfferList from './OfferList';
import Spinner from './Spinner';

const FINANCE_PATH = '/finance';
const OFFERS_PATH = '/offers';

type Props = {
  searchString: string;
  ledExperience?: boolean;
  showCountMessage?: boolean;
  urlBuilder?: (row: App.Offer) => string;
  onRowClick?: (row: App.Offer) => void;
};

type State = {
  loading: boolean;
  offerData: { total: number; result: Array<App.Offer> };
  error: string;
  page: number;
};

function OfferListContainer(props: Props) {
  const location = useLocation();
  const { push } = useAppNavigation();
  const sizePerPage = 10;
  const isOffersPage = location.pathname === OFFERS_PATH;

  const [state, setState] = useState<State>({
    loading: false,
    offerData: null,
    error: '',
    page: currentPage(),
  });

  function currentPage() {
    const { page } = parseSearchString(location.search);
    return parseInt(page as string) || 1;
  }

  const onPageChange = (page: number) => {
    setState((prev) => ({ ...prev, page }));
    const newLocation = addQuery(location, { page });
    push(newLocation);
  };

  const updateOffers = (searchString: string, ledExperience: boolean) => {
    setState((prev) => ({ ...prev, offerData: null, loading: true, error: '' }));

    const service = ledExperience ? LedOffersService : OffersService;

    service
      .getFilterOffers({
        page: state.page,
        limit: sizePerPage,
        queryString: searchString,
        filter: location.pathname === FINANCE_PATH ? 'finance' : undefined,
      })
      .then((offers) => {
        const offersData = offers;

        if (!isOffersPage || !offersData.total) {
          setState((prev) => ({ ...prev, offerData: offers, loading: false }));
        }

        const vendorIds = offers.result.map((x) => x.vendor_account_id).join(',');

        if (!vendorIds) {
          return;
        }

        VendorsService.getVendorsByIds(vendorIds).then((vendors) => {
          const vendorsMap = new Map();

          vendors.result.forEach((vendor) => vendorsMap.set(vendor.sfid, vendor.name));

          offersData.result = offers.result.map((offer) =>
            Object.assign(offer, {
              vendor_name: vendorsMap.get(offer.vendor_account_id) ?? '-',
            }),
          );

          setState((prev) => ({ ...prev, offerData: offersData, loading: false }));
        });
      })
      .catch((error) => {
        setState((prev) => ({ ...prev, error: error.message, loading: false }));
      });
  };

  useEffect(() => {
    updateOffers(props.searchString, props.ledExperience);
  }, [props.searchString, state.page]);

  const errorMessage = state.error;

  if (state.loading) {
    return <Spinner />;
  }

  if (!state.offerData) {
    return null;
  }

  const offerPluralized = pluralize('offer', state.offerData.total, true);

  const paginationCount = `${offerPluralized} found by your search criteria`;

  return (
    <>
      {props.showCountMessage && <Typography variant="h4">{paginationCount}</Typography>}

      {errorMessage && (
        <Alert severity="error" onClose={() => setState((prev) => ({ ...prev, error: '' }))}>
          {errorMessage}
        </Alert>
      )}

      <OfferList
        sizePerPage={sizePerPage}
        offers={state.offerData.result}
        total={state.offerData.total}
        page={state.page}
        isOffersPage={isOffersPage}
        urlBuilder={props.urlBuilder}
        onRowClick={props.onRowClick}
        onPageChange={onPageChange}
      />
    </>
  );
}

export default OfferListContainer;
